import { useTranslation, UseTranslationOptions, UseTranslationResponse } from 'react-i18next';
import type { FlatNamespace, KeyPrefix, TFunction } from 'i18next';
import type { $Tuple } from 'react-i18next/helpers';
import { FallbackNs } from 'react-i18next/index';
import { selectCurrentMerchant } from '../auth/authSlice';
import { useAppSelector } from '../../../app/hooks';

export const getSlug = (value: string) => String(value)
    .normalize('NFKD') // split accented characters into their base characters and diacritical marks
    .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
    .trim() // trim leading or trailing whitespace
    .toLowerCase() // convert to lowercase
    .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens

const translateWithMerchantContext = (context: string, tFunc: any) => {
    const mt = (key: any, options?: any) => {
        const contextAwareOptions = { ...options, context: getSlug(context) };
        return tFunc(key, contextAwareOptions);
    };
    return mt;
};

export default function useMerchantTranslation<
    Ns extends FlatNamespace | $Tuple<FlatNamespace> | undefined = undefined,
    KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined,
>(
    ns?: Ns,
    options?: UseTranslationOptions<KPrefix>,
): UseTranslationResponse<FallbackNs<Ns>, KPrefix> {
    const merchant = useAppSelector(selectCurrentMerchant);
    const translationContext = getSlug(merchant?.name ?? 'Bodd');
    const translator = useTranslation(ns, options);
    const { t, ...rest } = translator;
    const wrappedFunction = translateWithMerchantContext(translationContext, t) as TFunction<any, KPrefix>;

    return { t: wrappedFunction, ...rest };
}
